import { useState } from 'react';
import {
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Button,
  Tabs,
  Empty
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ToolOutlined,
  WarningOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { trpc } from '../lib/api';
import { WorkScheduleDetailModal } from '../components/WorkSchedule/DetailModal';
import { UserDisplay, PageHeader, PageContainer } from '../components/common';
import { HelpButton } from '../components/help/HelpButton';
import {
  getWorkScheduleStatusLabel,
  getWorkScheduleStatusColor
} from '../constants/workScheduleStatus';
import type { WorkScheduleDetail } from '../components/WorkSchedule/DetailModal';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

/**
 * 個人タスクダッシュボード
 * - 自分の担当作業
 * - 承認待ちリスト
 * - 最近完了した作業
 */
export default function MyTasksPage() {
  const [selectedSchedule, setSelectedSchedule] = useState<WorkScheduleDetail | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // 個人タスク一覧を取得
  const { data: myTasks, isLoading, refetch } = trpc.workSchedule.getMyTasks.useQuery();

  const myAssignedTasks = myTasks?.myAssignedTasks ?? [];
  const pendingApprovals = myTasks?.pendingApprovals ?? [];
  const completedRecently = myTasks?.completedRecently ?? [];

  const handleRowClick = (record: WorkScheduleDetail) => {
    setSelectedSchedule(record);
    setDetailModalOpen(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedSchedule(null);
  };

  const handleChanged = async () => {
    await refetch();
  };

  // テーブルカラム定義
  type WorkScheduleStatus = WorkScheduleDetail['status'];
  type AssignedUser = NonNullable<WorkScheduleDetail['assignedUsers']>[number];

  const columns: ColumnsType<WorkScheduleDetail> = [
    {
      title: 'タイトル',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: WorkScheduleStatus) => (
        <Tag color={getWorkScheduleStatusColor(status)}>
          {getWorkScheduleStatusLabel(status)}
        </Tag>
      )
    },
    {
      title: '予定日時',
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      width: 180,
      render: (date: string) => moment(date).format('YYYY/MM/DD HH:mm')
    },
    {
      title: '優先度',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: string) => {
        const colorMap: Record<string, string> = {
          urgent: 'red',
          high: 'orange',
          medium: 'blue',
          low: 'green'
        };
        return <Tag color={colorMap[priority] ?? 'default'}>{priority}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: unknown, record) => (
        <Button
          type="link"
          size="small"
          icon={<FileTextOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(record);
          }}
        >
          詳細
        </Button>
      )
    }
  ];

  // 承認用カラム（承認者情報追加）
  const approvalColumns: ColumnsType<WorkScheduleDetail> = [
    ...columns.slice(0, -1),
    {
      title: '担当者',
      dataIndex: 'assignedUsers',
      key: 'assignedUsers',
      width: 150,
      render: (assignedUsers?: WorkScheduleDetail['assignedUsers']) => {
        if (assignedUsers && assignedUsers.length > 0) {
          return (
            <Space size="small" wrap>
              {assignedUsers.slice(0, 2).map((user: AssignedUser) => (
                <UserDisplay key={user.id} userId={user.id} user={user} size="small" />
              ))}
              {assignedUsers.length > 2 && <Text type="secondary">+{assignedUsers.length - 2}</Text>}
            </Space>
          );
        }
        return <Text type="secondary">未割り当て</Text>;
      }
    },
    columns[columns.length - 1]
  ];

  const tablePagination = {
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100']
  };

  return (
    <PageContainer>
      {/* ヘッダー */}
      <PageHeader
        title="マイタスク"
        subtitle="自分の担当作業と承認待ちの作業を管理できます"
        extra={<HelpButton topicId="maintenance-my-tasks" />}
      />

      {/* サマリーカード */}
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="担当作業"
              value={myAssignedTasks.length}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="承認待ち"
              value={pendingApprovals.length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="最近完了"
              value={completedRecently.length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* タブ */}
      <Card>
        <Tabs
          defaultActiveKey="assigned"
          items={[
            {
              key: 'assigned',
              label: (
                <span>
                  <ToolOutlined />
                  担当作業 ({myAssignedTasks.length})
                </span>
              ),
              children: (
                <Table<WorkScheduleDetail>
                  dataSource={myAssignedTasks}
                  columns={columns}
                  rowKey="id"
                  loading={isLoading}
                  pagination={tablePagination}
                  onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                    style: { cursor: 'pointer' }
                  })}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="担当作業がありません"
                      />
                    )
                  }}
                />
              )
            },
            {
              key: 'pending-approval',
              label: (
                <span>
                  <WarningOutlined />
                  承認待ち ({pendingApprovals.length})
                </span>
              ),
              children: (
                <Table<WorkScheduleDetail>
                  dataSource={pendingApprovals}
                  columns={approvalColumns}
                  rowKey="id"
                  loading={isLoading}
                  pagination={tablePagination}
                  onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                    style: { cursor: 'pointer' }
                  })}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="承認待ちの作業がありません"
                      />
                    )
                  }}
                />
              )
            },
            {
              key: 'completed',
              label: (
                <span>
                  <CheckCircleOutlined />
                  最近完了 ({completedRecently.length})
                </span>
              ),
              children: (
                <Table<WorkScheduleDetail>
                  dataSource={completedRecently}
                  columns={columns}
                  rowKey="id"
                  loading={isLoading}
                  pagination={tablePagination}
                  onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                    style: { cursor: 'pointer' }
                  })}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="最近完了した作業がありません"
                      />
                    )
                  }}
                />
              )
            }
          ]}
        />
      </Card>

      {/* 詳細モーダル */}
      <WorkScheduleDetailModal
        open={detailModalOpen}
        schedule={selectedSchedule}
        onClose={handleDetailModalClose}
        onChanged={handleChanged}
      />
    </PageContainer>
  );
}
